import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { ThemeContext } from './context/ThemeContext';
import Navbar from './components/Navbar';
import ChatContainer from './components/ChatContainer';
import ChatInput from './components/ChatInput';
import ThemeToggle from './components/ThemeToggle';
import ConnectDriveModal from './components/ConnectDriveModal';
import RegistrationModal from './components/RegistrationModal';
import ProfileDrawer from './components/ProfileDrawer';
import Spinner from './components/Spinner';
import { queryDocuments } from './services/api';
import './styles/modal.css';
import './styles/theme.css';
import './index.css';
import { useStytch, useStytchSession } from '@stytch/react';
import config from './config';

function App() {
  const { isDarkMode } = useContext(ThemeContext);
  const [isConnectDriveModalOpen, setIsConnectDriveModalOpen] = useState(false);
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [preferences, setPreferences] = useState('');
  const [showSpinner, setShowSpinner] = useState(false);
  const [spinnerMessage, setSpinnerMessage] = useState('');
  const [hasSession, setHasSession] = useState(false);
  const stytch = useStytch();
  const stytchSession = useStytchSession();
  const [email, setEmail] = useState(null);
  const [syncedDrives, setSyncedDrives] = useState(new Set());
  const [user, setUser] = useState({});
  const isAuthenticating = useRef(false);
  const [loadingTasks, setLoadingTasks] = useState(new Set());
  const [denySubmitDueToDriveSync, setDenySubmitDueToDriveSync] = useState(true);
  const [errorRetryCount, setErrorRetryCount] = useState({
    auth: 0,
    sync: 0,
    query: 0
  });

  // MARK: Hooks
  useEffect(() => {
    document.body.className = isDarkMode ? 'dark-theme' : 'light-theme';
  }, [isDarkMode]);

  useEffect(() => {
    const stytchSessionUseEffect = async () => {
      setLoadingTasks(prev => new Set(prev).add("stytchSessionUpdates"));
      setSpinnerMessage("Session updates");

      try {
        // Sets email if session or token exists
        await handleAuthentication();
        
      } catch (error) {
        console.error("Authentication error:", error);
        
        // Add user-friendly error message based on error type
        const errorMessage = getAuthErrorMessage(error);
        setMessages(prev => [...prev, { text: errorMessage, isUser: false }]);
        
        setIsRegistrationModalOpen(true);
      } finally {
        setLoadingTasks(prev => {
          const newSet = new Set(prev);
          newSet.delete("stytchSessionUpdates");
          return newSet;
        });
      }
    };

    stytchSessionUseEffect();
  }, [stytchSession]);

  // Helper function to get appropriate auth error messages
  const getAuthErrorMessage = (error) => {
    if (error.status === 401) {
      return "Your session has expired. Please log in again.";
    } else if (error.status === 429) {
      return "Too many login attempts. Please try again in a few minutes.";
    } else if (error.message?.includes("network") || !navigator.onLine) {
      return "Network connection issue. Please check your internet connection and try again.";
    } else {
      return "Unable to authenticate. Please try logging in again.";
    }
  };

  useEffect(() => {
    const emailUseEffect = async () => {
      setLoadingTasks(prev => new Set(prev).add("emailUpdates"));
      setSpinnerMessage("Email updates");

      try {
        if (email) {
          setIsRegistrationModalOpen(false);
          await handleUser();
        } else {
          console.log("need email to make set user data");
          setIsRegistrationModalOpen(true);
        }
      } catch (error) {
        console.error("Email updates error:", error);
        
        // Show user-friendly message
        const errorMessage = error.message || "Error updating user information. Please try again.";
        setMessages(prev => [...prev, { text: errorMessage, isUser: false }]);
        
        setIsRegistrationModalOpen(true);
      } finally {
        setLoadingTasks(prev => {
          const newSet = new Set(prev);
          newSet.delete("emailUpdates");
          return newSet;
        });
      }
    };

    emailUseEffect();
  }, [email]);

  useEffect(() => {
    // Update status when syncedDrives changes
    if (syncedDrives.size > 0) {
      console.log("Drives synced:", Array.from(syncedDrives));
      setDenySubmitDueToDriveSync(false);
    } else {
      setDenySubmitDueToDriveSync(true);
    }
  }, [syncedDrives]);

  // Helper function for Google authorization
  const startGoogleAuthAndDriveSyncFlow = async () => {
    console.log("Starting Google auth flow");
    setShowSpinner(true);
    setSpinnerMessage("Syncing files");
    
    try {
      // Use the user ID from our stored user object
      const userId = user?.id;
      if (!userId) {
        throw new Error("User ID not available");
      }
      
      console.log("userId ln 145", userId);
      const authorizationUrl = await fetch(`${config.api.url}/authorizationUrl?userId=${userId}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json'
          }
        })
        .then(res => res.json())
        .then(data => {
          return data

        })
        .catch(error => {
          console.error("Error fetching authorization URL:", error);
          throw new Error("Error fetching authorization URL");
        });

      console.log("authorizationUrl ln 159", authorizationUrl);
      
      // Redirect to Google authorization URL if valid
      if (authorizationUrl) {
        window.location.href = authorizationUrl;
      } else {
        // Handle case where URL is returned but is not valid
        console.error("Invalid authorization URL received");
        setMessages(prev => [...prev, { 
          text: "Error starting Google Drive sync. Please try again or contact support if the problem persists.", 
          isUser: false 
        }]);
      }
      // NEXT:
    //   if (authorizationUrl) {
    //     window.location.href = authorizationUrl;
    //   }

    //   const userData = await response.json();
    //   const drivesSynced = userData.drivesSynced;
    //   if (drivesSynced) {
    //     // Update the Set with a new Set that includes the new drive
    //     setSyncedDrives(prevDrives => {
    //       const newDrives = new Set(prevDrives);
    //       newDrives.add("gdrive");
    //       return newDrives;
    //     });
        
    //     setMessages(prev => [...prev, {
    //       text: "Your Google Drive has been synced successfully!", 
    //       isUser: false 
    //     }]);
        
    //     // Reset error retry count on success
    //     setErrorRetryCount(prev => ({...prev, sync: 0}));
    //   } else {
    //     throw new Error("Drive sync completed but no files were found. Please check your Google Drive has documents.");
    //   }
    // } catch (error) {
    //   console.error("Error starting sync:", error);
      
    //   // Increment error count for sync
    //   setErrorRetryCount(prev => ({...prev, sync: prev.sync + 1}));
      
    //   // Provide specific guidance based on error
    //   let errorMessage = "Error syncing your files. ";
      
    //   if (error.message?.includes("User ID not available")) {
    //     errorMessage += "Please try logging in again.";
    //   } else if (error.message?.includes("Authorization expired")) {
    //     errorMessage += "Your session has expired. Please log in again.";
    //   } else if (error.message?.includes("Too many sync requests")) {
    //     errorMessage += `Please wait a few minutes before trying again. (Attempt ${errorRetryCount.sync + 1})`;
    //   } else if (error.message?.includes("network") || !navigator.onLine) {
    //     errorMessage += "Please check your internet connection and try again.";
    //   } else if (error.message?.includes("no files were found")) {
    //     errorMessage += "Make sure you have documents in your Google Drive.";
    //   } else {
    //     errorMessage += "Please try again later or leave feedback if the problem persists.";
    //   }
      
    //   setMessages(prev => [...prev, { 
    //     text: errorMessage, 
    //     isUser: false 
    //   }]);
    } finally {
      setShowSpinner(false);
      setSpinnerMessage("");
    }
  };

  // By the end of authentication the user's email is set in the state
  const handleAuthentication = async() => {
    if (loadingTasks.has("authenticateUser")) {
        return;
    }

    addLoadingTask("authenticateUser");

    setShowSpinner(true);
    console.log("Authenticating");

    console.log("checking stytchSession ", stytchSession);
    if (stytchSession.session) {
        console.log("stytchSession exists:", stytchSession);
        setHasSession(true);

        // Fetch user's email from the session
        stytch.user.get({
            session_jwt_options: {
                enable_secure_cookies: true,
                cookie_options: {
                    http_only: true,
                    same_site: "strict",
                    secure: true
                }
            }
        })
          .then((response) => {
              return response.emails[0].email;
          }).then((email) => {
            if (email.length > 0) {
              setEmail(email);
              setShowSpinner(false);
              setIsLoading(false);
              removeLoadingTask("authenticateUser");

              return true
            } else {
              throw new Error("No email found in session");
            }
          })
          .catch((error) => {
              console.error("Error in session flow:", error);
              console.error("Error details:", error.message);
              console.error("Error stack:", error.stack);

              // Handle cleanup in case of error
              setShowSpinner(false);
              console.log("ShowSpinner set to:", showSpinner);
              console.log("error end session authentication ln 136: email: ", email);
              setIsLoading(false);
              removeLoadingTask("authenticateUser");
          })
    }

    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    console.log("checking token ", token);
    if (token && !stytchSession.session) {
        return  stytch.magicLinks.authenticate(token, {
            session_duration_minutes: 60,
            session_jwt_options: {
                enable_secure_cookies: true,
                cookie_options: {
                    http_only: true,
                    same_site: "strict",
                    secure: true
                }
            }
        })
          .then(() => {
            console.log("Token authentication successful");
            setHasSession(true);

            return stytch.user.get();
          }).then((response) => {
              const userEmail = response.emails[0].email;
              setEmail(userEmail);
          })
          .catch((error) => {
              console.error("Authentication failed:", error);
              setIsRegistrationModalOpen(true);
              removeLoadingTask("authenticateUser");
          }).finally(() => {
              setShowSpinner(false);
              removeLoadingTask("authenticateUser");
          });
    } else if (!token && !stytchSession.session) {
        console.log("No token and no session, showing registration modal");

        // Clear user data
        setEmail(null);
        setUser({});
        setSyncedDrives(new Set());
        setHasSession(false);
        setMessages([]);
        
        setIsRegistrationModalOpen(true);
        removeLoadingTask("authenticateUser");
    }
  };

  const getSyncedDrives = async() => {
    try {
      const response = await fetch(`${config.api.url}/syncedDrives?userId=${user.id}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Authorization expired. Please log in again.");
        } else {
          throw new Error(`Server error (${response.status}). Please try again later.`);
        }
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching synced drives:", error);
      
      let errorMessage = "Error retrieving your synced drives. ";
      if (error.message?.includes("Authorization expired")) {
        errorMessage += "Please log in again.";
      } else if (error.message?.includes("network") || !navigator.onLine) {
        errorMessage += "Please check your internet connection and try again.";
      } else {
        errorMessage += "Please try again later.";
      }
      
      setMessages(prev => [...prev, { text: errorMessage, isUser: false }]);
      return { status: "error", syncedDrives: [] };
    }
  };
  

  const handleUser = async() => {
    addLoadingTask("handleUser");
    if (!email) {
      console.log("No email found, showing registration modal");
      setMessages(prev => [...prev, { text: "Please sign in with your email to continue.", isUser: false }]);
      setIsRegistrationModalOpen(true);
      return;
    }

    return await fetch(`${config.api.url}/users`, {
      method: 'POST',
      headers: {
          'Content-Type': 'application/json',
          'x-api-key': config.api.key
      },
      body: JSON.stringify({ email })
    }).then((response) => {
      if (!response.ok) {
        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After') || 60;
          throw new Error(`Rate limit exceeded. Please try again after ${retryAfter} seconds.`);
        }
        throw new Error(`Server error (${response.status}). Please try again later.`);
      }
      return response.json();
    }).then((data) => {
      setUser(data);
      if (data.syncedDrives.length > 0) {
        // Convert array to Set
        setSyncedDrives(new Set(data.syncedDrives));
        setDenySubmitDueToDriveSync(false);
        console.log("syncedDrives set");
      } else {
        console.log("no synced drives");
        setSyncedDrives(new Set()); // Ensure it's an empty Set
      }
    }).catch((error) => {
      console.error("Authentication failed:", error);
      setIsRegistrationModalOpen(true);
      removeLoadingTask("handleUser");
    }).finally(() => {
      setIsRegistrationModalOpen(false);
      setShowSpinner(false);
      removeLoadingTask("handleUser");
    });
  };

  const handleSubmit = async (queryText) => {
    addLoadingTask("handleSubmit");
    setSpinnerMessage("Submitting your message");

    function removeHandleSubmitTaskAndResetSpinnerMessage() {
      removeLoadingTask("handleSubmit");
      setSpinnerMessage("");
    }

    if (!hasSession) {
      setMessages(prev => [...prev, { 
        text: "You need to be logged in to use Svount. Please sign in to continue.", 
        isUser: false 
      }]);
      setIsRegistrationModalOpen(true);
      removeHandleSubmitTaskAndResetSpinnerMessage();
      return;
    }

    if (!email) {
      setMessages(prev => [...prev, { text: "You need to be logged in to use Svount.", isUser: false }]);
      setIsRegistrationModalOpen(true);
      removeHandleSubmitTaskAndResetSpinnerMessage();
      return;
    }

    if (!queryText.trim()) {
      removeHandleSubmitTaskAndResetSpinnerMessage();
      return;
    }
    
    // Check if user has synced drives
    if (denySubmitDueToDriveSync) {
      // Use window.confirm instead of custom modal
      const userChoice = window.confirm(
        "You need to sync your files before chatting. Would you like to sync now?"
      );
      
      console.log("User choice:", userChoice);
      if (userChoice) {
        // User clicked OK (Sync)
        // startGoogleAuthFlow();
        syncFiles();
      } else {
        // User clicked Cancel
        setMessages(prev => [...prev, { text: queryText, isUser: true }]);
        setMessages(prev => [...prev, { 
          text: "I need access to your files to answer questions. Please sync your files when you're ready.", 
          isUser: false 
        }]);
      }

      removeHandleSubmitTaskAndResetSpinnerMessage
      return;
    }
    
    processQuery(queryText).finally(() => {
      removeHandleSubmitTaskAndResetSpinnerMessage();
    });
  };

  const addLoadingTask = useCallback((taskName) => {
    setLoadingTasks(prev => new Set(prev).add(taskName));
  }, []);

  const removeLoadingTask = useCallback((taskName) => {
      setLoadingTasks(prev => {
          const newSet = new Set(prev);
          newSet.delete(taskName);
          return newSet;
      });
  }, []);

  // Function to process the actual query
  const processQuery = async (queryText) => {
    setMessages(prev => [...prev, { text: queryText, isUser: true }]);
    setIsLoading(true);
    
    try {
      if (!user.id) {
        throw new Error('User ID not found. Please log in again.');
      }

      const result = await queryDocuments(user.id, queryText, preferences);
      
      if (!result || !result.response || !result.response.choices || !result.response.choices[0]) {
        throw new Error('Received invalid response from server');
      }
      
      const svountResponseMessage = result.response.choices[0].message.content;
      if (svountResponseMessage) {
        setMessages(prev => [...prev, { text: svountResponseMessage, isUser: false }]);
        
        // Reset error retry count on success
        setErrorRetryCount(prev => ({...prev, query: 0}));
      } else {
        throw new Error('No answer found in the response');
      }
    } catch (error) {
      console.error('Query error:', error);
      
      // Increment error retry count
      setErrorRetryCount(prev => ({...prev, query: prev.query + 1}));
      
      // Create appropriate error message based on error type
      let errorMessage = "";
      
      if (error.response) {
        // API responded with error status
        if (error.response.status === 429) {
          errorMessage = `Rate limit exceeded. Please try again in a few minutes. (Attempt ${errorRetryCount.query + 1})`;
        } else if (error.response.status === 401 || error.response.status === 403) {
          errorMessage = "Your session has expired. Please log in again.";
        } else if (error.response.status >= 500) {
          errorMessage = "Server error. Our team has been notified. Please try again later.";
        } else {
          errorMessage = `Error (${error.response.status}): ${error.response.data?.message || 'Unknown error'}`;
        }
      } else if (error.message?.includes('User ID not found')) {
        errorMessage = "Your session appears to have expired. Please log in again.";
      } else if (error.message?.includes('network') || !navigator.onLine) {
        errorMessage = "Network error. Please check your internet connection and try again.";
      } else if (error.message?.includes('No answer found') || error.message?.includes('invalid response')) {
        errorMessage = "I couldn't find an answer in your files. Try asking a different question or syncing more files.";
      } else {
        errorMessage = "Error processing your question. Please try again.";
      }
      
      setMessages(prev => [...prev, { text: errorMessage, isUser: false }]);
    } finally {
      setIsLoading(false);
    }
  };

  const syncFiles = async() => {
    addLoadingTask("syncFiles");
    setSpinnerMessage("Syncing files");

    function removeHandleSyncFilesTaskAndResetSpinnerMessage() {
      removeLoadingTask("syncFiles");
      setSpinnerMessage("");
    }

    if (!hasSession) {
      setMessages(prev => [...prev, { 
        text: "You need to be logged in to sync files. Please sign in to continue.", 
        isUser: false 
      }]);
      setIsRegistrationModalOpen(true);
      removeHandleSyncFilesTaskAndResetSpinnerMessage();
      return;
    }

    if (email) {
      try {
        await startGoogleAuthAndDriveSyncFlow();
        // Check if 'gdrive' is in the Set
        if (syncedDrives.has("gdrive")) {
          setMessages(prev => [...prev, {
            text: "Your files are synced and ready for questions!",
            isUser: false
          }]);
          setDenySubmitDueToDriveSync(false);
        } else {
          throw new Error("File sync failed. Please try again.");
        }
      } catch (error) {
        console.error("Sync files error:", error);
        
        let errorMessage = "Error syncing your files. ";
        if (error.message?.includes("network") || !navigator.onLine) {
          errorMessage += "Please check your internet connection and try again.";
        } else {
          errorMessage += "Please try again or leave feedback if the problem persists.";
        }
        
        setMessages(prev => [...prev, { text: errorMessage, isUser: false }]);
      } finally {
        removeHandleSyncFilesTaskAndResetSpinnerMessage();
      }
    } else {
      setIsRegistrationModalOpen(true);
    }
    
    removeHandleSyncFilesTaskAndResetSpinnerMessage();
  };

  const handleLogout = () => {
    stytch.session.revoke()
      .then(() => {
        // Clear user data
        setHasSession(false);
        setEmail(null);
        setUser({});
        setSyncedDrives(new Set());
        setMessages([]);
      })
      .catch(err => {
        console.error("Logout error:", err);
      })
      .finally(() => {
        window.location.reload();
      });
  };

  const handleDeleteAccount = async () => {
    try {
      if (!user || !user.id) {
        console.log("No user ID available");
        throw new Error("No user ID available");
      }
      
      const response = await fetch(`${config.api.url}/users?userId=${user.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

      // Log out after successful deletion
      await stytch.session.revoke();
      
      // Clear user data
      setHasSession(false);
      setEmail(null);
      setUser({});
      setSyncedDrives(new Set());
      setMessages([{
        text: "Your account has been deleted successfully.", 
        isUser: false
      }]);
      
      // Reload page after a short delay
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      console.error("Delete account error:", error);
      setMessages(prev => [...prev, { 
        text: "Failed to delete account. Please try again later.", 
        isUser: false 
      }]);
    }
  };

  return (
    <div className={`app ${isDarkMode ? 'dark' : 'light'}`}>
      <Navbar 
        setShowSpinner={setShowSpinner}
        setSpinnerMessage={setSpinnerMessage}
        setIsRegistrationModalOpen={setIsRegistrationModalOpen}
        hasSession={hasSession}
        setHasSession={setHasSession}
        syncFiles={syncFiles}
        email={email}
      />
      
      <ChatContainer messages={messages} />
      
      <ChatInput onSubmit={handleSubmit} isLoading={isLoading} />
      
      <ProfileDrawer 
        preferences={preferences}
        setPreferences={setPreferences}
        onLogout={handleLogout}
        onDeleteAccount={handleDeleteAccount}
      />
      
      <ConnectDriveModal 
        isOpen={isConnectDriveModalOpen}
        onClose={() => setIsConnectDriveModalOpen(false)}
      />

      <RegistrationModal 
        isOpen={isRegistrationModalOpen}
        onClose={() => setIsRegistrationModalOpen(false)}
      />
      
      {loadingTasks.size > 0 && <Spinner message={spinnerMessage} />}
    </div>
  );
}

export default App; 